/** @jsx jsx */
import { jsx, useThemeUI } from "theme-ui";
import { Fragment, useEffect } from "react";
import { UAnchorLink } from "../../../components/UAnchorLink";
import { Box, Flex, Heading, Text } from "rebass";
import Hero from "../../../components/Hero";
import ProjectTitleListing from "../../../components/ProjectTitleListing";
import ProjectFadeInTitle from "../../../components/ProjectFadeInTitle";
import BackToTop from "../../../components/BackToTop";
import { StaticImage } from "gatsby-plugin-image";
import PlaneScene from "../../../components/PlaneScene";
//import Card from "../../../components/Card";
//import { Modal } from "../../../components/Modal";
//import { useModal } from "../../../components/useModal";
import Carousel from "../../../components/Carousel";
//import Close from "../../../icons/Close";
import CompanyProjects from "../../../components/CompanyProjects";
import Layout from "../../../layouts/";
import Header from "../../../components/Header";
import Footer from "../../../components/Footer";
import ThemeColorSetter from "../../../components/ThemeColorSetter";

//Assets
import img1 from "../../../img/ing/documentation-global-header-01.jpg";
import img2 from "../../../img/ing/documentation-global-header-02.jpg";
import img3 from "../../../img/ing/documentation-global-header-03.jpg";
import img4 from "../../../img/ing/documentation-global-header-04.jpg";
import img5 from "../../../img/ing/documentation-global-header-05.jpg";

//Videos
//import HowToExpressive from "../../../img/ing/how-to-expressive.mp4";

function ProjectINGPatternDocs() {
  const context = useThemeUI();
  const { theme } = context;
  {
    /* const modalPersona = useModal(); */
  }

  //const modalMobileFirst = useModal();

  let pathname = typeof window !== "undefined" ? window.location.pathname : "";
  const section1 = pathname + "#section1";
  const section2 = pathname + "#section2";
  const section3 = pathname + "#section3";
  const section4 = pathname + "#section4";

  const projectBgColor = theme?.colors?.ingProjectBgPD as string;
  const projectTextColor = theme?.colors?.ingProjectTextPD as string;
  const themeColor = theme?.colors?.ingProjectBgPD as string;
  const themeColorBg = theme?.colors?.background as string;

  useEffect(() => {
    const handleScroll = () => {
      const heroSection = document.getElementById("hero-section");
      if (heroSection) {
        const rect = heroSection.getBoundingClientRect();
        if (rect.bottom > 0) {
          document.body.classList.add("hero-visible");
          document.body.classList.remove("hero-hidden");
        } else {
          document.body.classList.add("hero-hidden");
          document.body.classList.remove("hero-visible");
        }
      } else {
        document.body.classList.add("hero-hidden");
        document.body.classList.remove("hero-visible");
      }
    };

    const heroSection = document.getElementById("hero-section");
    if (heroSection) {
      window.addEventListener("scroll", handleScroll);
      handleScroll(); // Initial check
      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    } else {
      document.body.classList.add("hero-hidden");
      document.body.classList.remove("hero-visible");
    }
  }, []);

  return (
    <Layout>
      <ThemeColorSetter color={themeColor} colorBg={themeColorBg} />
      <main>
        <Header bgColor={projectBgColor} textColor={projectTextColor} />
        {/* On page navigation */}
        <Flex justifyContent="center">
          <Box variant="onPageNav" className="page-nav">
            <UAnchorLink
              to={section1}
              sx={{
                variant: "buttons.outline",
                backgroundColor: "transparent",
              }}
              title="Overview"
            >
              Overview
            </UAnchorLink>
            <UAnchorLink
              to={section2}
              sx={{
                variant: "buttons.outline",
                backgroundColor: "transparent",
              }}
              title="Problem statement"
            >
              Problem
            </UAnchorLink>
            <UAnchorLink
              to={section3}
              sx={{
                variant: "buttons.outline",
                backgroundColor: "transparent",
              }}
              title="Approach"
            >
              Approach
            </UAnchorLink>
            <UAnchorLink
              to={section4}
              sx={{
                variant: "buttons.outline",
                backgroundColor: "transparent",
              }}
              title="Result"
            >
              Result
            </UAnchorLink>
          </Box>
        </Flex>

        {/* Hero / Overview */}
        <div id="section1">
          <Hero
            projectType={"Showcase"}
            title={"Pattern"}
            titleRow={"Documentation"}
            titleClassName={"uppercase"}
            productType={"Design System, Documentation"}
            subtitle={
              "Beyond components, designing shared experiences at scale."
            }
            projectBg={projectBgColor}
            projectText={projectTextColor}
            year={"2025 — 2026"}
            role={<Fragment>Senior UX Designer</Fragment>}
            company={"ING"}
            roleDesc={
              <Fragment>
               As ING's design system matured, components alone were no longer enough. Teams knew how to use buttons and menus from the design system, but not always how those pieces should come together to create consistent experiences.
               To bridge that gap, I introduced pattern documentation—a layer that connects components into complete user journeys. More than documentation, patterns became a shared language for design and engineering, helping teams create familiar, scalable experiences across products, platforms, and markets.
              </Fragment>
            }
            disciplines={
              <Fragment>
                Product &mdash; Design &mdash; Development
              </Fragment>
            }
            challenge={
              <Fragment>
                As products and teams scaled, the same components were being assembled into different experiences. Navigation, search, onboarding, and other key interactions increasingly depended on individual interpretation rather than shared decisions.
                Without clear guidance at the pattern level, teams began interpreting experiences differently, making it harder to maintain a unified user experience, achieve design-code parity, and avoid unnecessary variation across products that needed to share an experience.
              </Fragment>
            }
            component={
              <PlaneScene
                coverColor={"#F7F4F1"}
                textColor={"black"}
                urlTexture={"/models/textures/ing-cover-documentation-front.jpg"}
                urlBackTexture={"/models/textures/ing-cover-documentation-back.jpg"}
              />
            }
          />
        </div>

        {/* Problem statement */}
        <div id="section2">
          <Flex
            flexWrap="wrap"
            justifyContent="center"
            variant="container"
            id="project-content"
          >
            <Box width={[1 / 1, 1 / 1, 1 / 1]} variant="containerBigTitle">
              <ProjectFadeInTitle
                word1={"Growing"}
                word2={"Up"}
                word2ClassName={"outline"}
                word3={"the System"}
                className={"uppercase"}
              />
            </Box>

            <Box variant="containerTxt">
              <Heading as="h3" variant="catHeading">
                Problem statement
              </Heading>
              <Text
                as="p"
                mb={[3,4]}
                variant="paragraphXL"
                fontWeight="medium"
              >
                ING required a solution to ensure that critical user experiences, including navigation, search, forms, and onboarding, consistently functioned across different products, markets, and platforms.
              </Text>
              <Text as="p" mb={[5, 6]}>
                This solution should maintain design-code parity, minimize interpretation during implementation, and transform recurring experience decisions into reusable patterns that can be consistently applied, managed, and scaled throughout the organization.
                </Text>
              <Box width={[1 / 1]} mb={[4, 5, 6]} variant="wrapperImgRound">
                <StaticImage
                  src="../../../img/ing/documentation-template.jpg"
                  alt="Documentation template"
                />
              </Box>
              <Heading as="h3" variant="catHeading">
                Challenge #1
              </Heading>
              <Heading as="h3" variant="bigTitleXS">
                Components alone don’t create alignment
              </Heading>
              <Text as="p" mb={[3, 4]}>
                The design system provided reusable components, but components alone could not guarantee a consistent user experience.
                Multiple teams could use the exact same building blocks and still create entirely different navigation flows, interaction models, and content hierarchies.              
              </Text>

              <Text as="p" mb={[3, 4]}>
                The challenge was no longer defining individual UI elements—it was establishing shared expectations for how those elements should work together.
                </Text>
              <Box width={[1 / 1]} mb={[4, 5, 6]} variant="wrapperImgRound">
                <StaticImage
                  src="../../../img/ing/documenting-the-documentation.jpg"
                  alt="Document template with annotations in Figma"
                />
              </Box>
              <Heading as="h3" variant="catHeading">
                Challenge #2
              </Heading>
              <Heading as="h3" variant="bigTitleXS">
                Non-Negotiable Decisions
              </Heading>
              <Text as="p" mb={[3, 4]}>
                Critical experiences, like navigation, need a shared foundation. Patterns allow local adaptation but establish common principles, behaviors, and structures. Without agreed-upon patterns, teams create variations that lead to fragmented experiences. <br/><br/>To achieve consistency, key behavioral decisions must be reusable and shared across products and markets.
              </Text>
              <Box width={[1 / 1]} mb={[4, 5, 6]} variant="wrapperImgRound">
                <StaticImage
                  src="../../../img/ing/decisions.jpg"
                  alt="Decisions trough democratic vote"
                />
              </Box>
              <Heading as="h3" variant="catHeading">
                Challenge #3
              </Heading>
              <Heading as="h3" variant="bigTitleXS">
                Complexity grows fast
              </Heading>
              <Text as="p" mb={[3, 4]}>
                Every new product, market, team, and implementation introduces opportunities for drifting.
                Without a shared framework, <strong>organizations gradually accumulate experience debt</strong>: 
                <ul>
                  <li>similar problems solved in different ways, </li>
                    <li>duplicate effort</li>
                    <li>increasing governance overhead.</li>
                </ul>
              </Text>
              <Heading as="h3" variant="catHeading">
                Challenge #4
              </Heading>
              <Heading as="h3" variant="bigTitleXS">
                Design Code Parity
              </Heading>
              <Text as="p" mb={[3, 4]}>
               While component documentation helps teams build individual pieces correctly, achieving design-code parity requires alignment on how those pieces work together. Behavior, hierarchy, interactions, and responsive adaptations all need to be defined beyond the component level.
              </Text>
              <Heading as="h3" variant="catHeading">
                Challenge #5
              </Heading>
              <Heading as="h3" variant="bigTitleXS">
                Achieve familiarity
              </Heading>
              <Text as="p" mb={[3, 4]}>
               Consistency provides the foundation for creating coherent experiences across products, platforms, and teams. The real outcome is familiarity. Familiar interactions reduce cognitive load, increase confidence, and help users accomplish tasks more efficiently.<br/>
               The challenge was creating experiences that felt immediately recognizable regardless of where users encountered them.
              </Text>
            </Box>
          </Flex>
        </div>

        <div id="section3">
          {/* The Solution */}
          <Flex
            flexWrap="wrap"
            py={[3, 4]}
            variant="container"
            justifyContent="center"
            mb={[4, 5]}
          >
            <Box width={[1 / 1, 1 / 1, 1 / 1]} variant="containerBigTitle">
              <ProjectFadeInTitle
                word1={"A System"}
                word2={"to"}
                word2ClassName={"outline"}
                word3={"Document"}
                word3ClassName={"outline"}
                className={"uppercase"}
              />
            </Box>

            <Box variant="containerTxt">
              <Heading as="h3" variant="catHeading">
                Solution
              </Heading>
              <Text as="p" pb={[3, 4]} mb={[3,4]} variant="paragraphXL">
                Rather than documenting only components, we introduced documentation for complete user experiences.
                Patterns document how multiple components work together to solve a recurring user need. 
              </Text> 
              <Text as="p" pb={[3, 4]} mb={[3,4]}> 
              Each pattern establishes shared expectations around structure, behavior, composition, accessibility, and implementation.
                The framework was built around a predictable information architecture that documents:
                <ul>
                  <li>Purpose and intent</li>
                  <li>Pattern architecture</li>
                  <li>Structure and composition</li>
                  <li>Behavior and interaction logic</li>
                  <li>Configurable options</li>
                  <li>Accessibility considerations</li>
                  <li>Best practices and examples</li>
                </ul>
              </Text>
              <Box width={[1 / 1]} mb={[5,6]} variant="wrapperImgRound">
                <StaticImage
                  src="../../../img/ing/header-architecture.jpg"
                  alt="Expressive engine"
                />
              </Box>
              {/* <Text as="span" variant="caption" mb={[4, 5, 6]}>
                Documentation templates with annotations where provided, to offer as much clarity as needed. 
              </Text> */}
            </Box>
          </Flex>

          <Flex
            flexWrap="wrap"
            py={[3, 4]}
            variant="container"
            justifyContent="center"
            mb={[4, 5]}
          >
            <Box width={[1 / 1, 1 / 1, 1 / 1]} variant="containerBigTitle">
              <ProjectFadeInTitle
                word1={"Less chatter,"}
                word1ClassName={"outline"}
                word2={"More pattern"}
                className={"uppercase"}
              />
            </Box>
            <Box variant="containerTxt">
              <Heading as="h2" variant="bigTitleXS" mb={[3]}>
                Global Header Pattern (web)
              </Heading>
              <Text as="p" pb={[3, 4]}>
               The Global Header became one of the first patterns documented using the new framework.
               Its purpose extends beyond navigation. The pattern helps users understand where they are, where they can go, and how to switch context when needed.
               The documentation defines how components such as the Utility Bar, Navigation Bar, Flyout Menus, Segment Switcher, Search, and Locale Selector work together as a single system.<br/> <br />
               By documenting both behavior and rationale, the pattern establishes a <strong>shared contract</strong> between design and engineering, ensuring navigation behaves consistently regardless of product or market.
              </Text>
              <Box width={[1 / 1]} mb={[2]} variant="wrapperImgRound">
                <StaticImage src="../../../img/ing/documentation-global-header-07.jpg" alt="Global Header Documentation" />
              </Box>
              <Text as="span" variant="caption">
                The document itself was a living truth, but it was the hidden alignments that made this approach the most valuable. 
              </Text>
            </Box>
          </Flex>
        </div>

        <div id="section4">
          <Flex
            flexWrap="wrap"
            py={[3, 4]}
            variant="container"
            justifyContent="center"
            mb={[4, 5]}
          >
            <Box variant="containerTxt">
              <Heading as="h2" variant="bigTitleXS">
                Result
              </Heading>
              <Text as="p">
                Pattern documentation transformed reusable components into reusable experiences.
                Instead of solving the same interaction problems repeatedly, teams gained a shared framework for designing, implementing, and evolving critical journeys.
                The result was:
                <ul>
                  <li>
                    Stronger alignment between design and engineering
                  </li>
                  <li>
                    Design-code parity
                  </li>
                  <li>
                    Reduced interpretation during implementation
                  </li>
                  <li>Consistency across products and markets</li>
                  <li>A scalable foundation for future patterns</li>
                </ul>
                Most importantly, patterns helped shift the organization from documenting interfaces to documenting experiences.
                Because components explain what things are.
                Patterns explain how things work together.
              </Text>
            </Box>
          </Flex>

          <Carousel
            img1={img1}
            img2={img2}
            img3={img3}
            img4={img4}
            img5={img5}
          />
          <Flex flexWrap="wrap" justifyContent="center" variant="container">
            {/* Deliverables */}
            <Box variant="containerTxt">
              <Heading as="h3" variant="catHeading">
                The End
              </Heading>
              <Heading as="h2" variant="bigTitleXS">
                Delivered
              </Heading>
              <ul>
                <li>Documentation Strategy</li>
                <li>Documenation Library</li>
                <li>
                  Cross-functional collaboration with Design, and Engineering
                </li>
              </ul>
              <Heading as="h2" variant="bigTitleXS" mt={[3, 4, 5]}>
                Personal Note
              </Heading>
              <Text as="p">
                In large, global companies, it's crucial to recognize that
                change occurs incrementally and can be challenging to implement.
                Celebrating small victories is essential to maintain momentum
                and encourage ongoing progress.
              </Text>
            </Box>
          </Flex>
        </div>
        <CompanyProjects title="ING" />
        <ProjectTitleListing />
        <BackToTop />
        <Footer />
      </main>
    </Layout>
  );
}

export default ProjectINGPatternDocs;
